import React from 'react';
import { Select, Slider, InputNumber, Button } from 'antd';
import { HighlightOutlined } from '@ant-design/icons';
import { useLayerProperitesContext } from '../../../../context/LayerPropertiesContext';
import IconControls from './components/IconControls';
import ColorField from './components/ColorField';
import { isBuiltInShape } from './components/builtInShapes';
import { getFillTypeOptions, getOutlineTypeOptions, lineFillTypeValue, lineOutlineTypeValue, clampLineWeight, MIN_LINE_WEIGHT, MAX_LINE_WEIGHT } from './lineSymbologyOptions';
import '../LayerProperties.scss';

interface SingleSymbologyProps {
  onRequestIconSelector: (source: 'recent' | 'styles' | 'upload') => void;
}

const SingleSymbology: React.FC<SingleSymbologyProps> = ({ onRequestIconSelector }) => {
  const {
    fill,
    fillType,
    fillOpacity,
    outline,
    outlineType,
    outlineOpacity,
    outlineWeight,
    lineWeight,
    layerType,
    selectedIcon,
    iconUploadKind,
    setState,
  } = useLayerProperitesContext();

  const isPointLayer = layerType === 'Point' || layerType === 'MultiPoint';
  const isLineLayer = !!layerType && layerType.includes('Line');
  const fillTypeOptions = getFillTypeOptions(isLineLayer);
  const outlineTypeOptions = getOutlineTypeOptions(isLineLayer);

  // US-101662 Scenario 3: when the user picked an uploaded image (PNG/JPG/SVG)
  // — i.e. NOT one of the built-in shapes — fill and outline controls must be
  // disabled. The shape variant (Scenario 2) keeps full controls.
  const treatAsImage = iconUploadKind === 'image' || (iconUploadKind !== 'shape' && selectedIcon != null && !isBuiltInShape(selectedIcon));
  const colorControlsDisabled = isPointLayer && treatAsImage;

  return (
    <div className="lp-symbology__stack">
      {/* ICON card — points only. Holds the icon picker, size and rotation. */}
      {isPointLayer && (
        <div className="lp-symbology__card">
          <IconControls
            typeKey="iconType"
            sizeKey="iconSize"
            rotationKey="iconRotation"
            iconKey="selectedIcon"
            onRequestIconSelector={onRequestIconSelector}
          />
        </div>
      )}

      {/* FILL card */}
      {!colorControlsDisabled && (
        <div className="lp-symbology__card">
          <div className="lp-symbology__card-header">FILL</div>

          <div className="lp-symbology__field-row">
            <ColorField value={fill} onChange={(hex) => setState((prev) => ({ ...prev, fill: hex }))} />
            {/* US-101662 Figma: a "match outline" tool that mirrors the fill
                colour to the outline (and vice-versa on the outline card). */}
            <Button
              type="text"
              icon={<HighlightOutlined />}
              className="lp-symbology__brush-btn"
              title="Copy to outline"
              onClick={() => setState((prev) => ({ ...prev, outline: prev.fill }))}
            />
            <InputNumber
              className="lp-symbology__opacity-input"
              value={fillOpacity}
              onChange={(e) => setState((prev) => ({ ...prev, fillOpacity: Number(e) }))}
              min={0}
              max={100}
              formatter={(v) => `${v}%`}
              parser={(v) => (v ? Number(String(v).replace('%', '')) : 0) as 0}
            />
          </div>

          <div className="lp-symbology__field-row">
            <span className="lp-symbology__sub-label">FILL TYPE</span>
            <Select
              className="lp-symbology__flex-input"
              placeholder="Solid"
              value={lineFillTypeValue(isLineLayer, fillType)}
              onChange={(value) => setState((prev) => ({ ...prev, fillType: value }))}
              options={fillTypeOptions}
            />
          </div>

          {isLineLayer && (
            <div className="lp-symbology__field-row">
              <span className="lp-symbology__sub-label">LINE WEIGHT</span>
              <Slider
                className="lp-symbology__flex-slider"
                min={MIN_LINE_WEIGHT}
                max={MAX_LINE_WEIGHT}
                step={0.5}
                value={lineWeight}
                onChange={(value) => setState((prev) => ({ ...prev, lineWeight: value }))}
              />
              <InputNumber
                className="lp-symbology__opacity-input"
                value={lineWeight}
                onChange={(e) => setState((prev) => ({ ...prev, lineWeight: clampLineWeight(e) }))}
                min={MIN_LINE_WEIGHT}
                max={MAX_LINE_WEIGHT}
                step={0.5}
                placeholder="pt"
              />
            </div>
          )}
        </div>
      )}

      {/* OUTLINE card */}
      {!colorControlsDisabled && (
        <div className="lp-symbology__card">
          <div className="lp-symbology__card-header">OUTLINE</div>

          {/* Outline colour + opacity inline (mirrors the FILL row). */}
          <div className="lp-symbology__field-row">
            <ColorField value={outline} onChange={(hex) => setState((prev) => ({ ...prev, outline: hex }))} />
            <Button
              type="text"
              icon={<HighlightOutlined />}
              className="lp-symbology__brush-btn"
              title="Copy to fill"
              onClick={() => setState((prev) => ({ ...prev, fill: prev.outline }))}
            />
            <InputNumber
              className="lp-symbology__opacity-input"
              value={outlineOpacity}
              onChange={(e) => setState((prev) => ({ ...prev, outlineOpacity: Number(e) }))}
              min={0}
              max={100}
              formatter={(v) => `${v}%`}
              parser={(v) => (v ? Number(String(v).replace('%', '')) : 0) as 0}
            />
          </div>

          <div className="lp-symbology__field-row">
            <span className="lp-symbology__sub-label">OUTLINE TYPE</span>
            <Select
              className="lp-symbology__flex-input"
              placeholder="Select Type"
              value={lineOutlineTypeValue(isLineLayer, outlineType)}
              onChange={(value) => setState((prev) => ({ ...prev, outlineType: value }))}
              options={outlineTypeOptions}
            />
          </div>

          <div className="lp-symbology__field-row">
            <span className="lp-symbology__sub-label">OUTLINE WEIGHT</span>
            <Slider
              className="lp-symbology__flex-slider"
              value={outlineWeight}
              onChange={(value) => setState((prev) => ({ ...prev, outlineWeight: value }))}
            />
            <InputNumber
              className="lp-symbology__opacity-input"
              value={outlineWeight}
              onChange={(e) => setState((prev) => ({ ...prev, outlineWeight: Number(e) }))}
              min={0}
              max={100}
              placeholder="pt"
            />
          </div>
        </div>
      )}

      {colorControlsDisabled && (
        // US-101662 Scenario 3: explicit hint so the user knows why Fill
        // and Outline aren't present for this icon kind.
        <div className="lp-symbology__image-hint">Fill and outline are not configurable when using an uploaded image icon.</div>
      )}
    </div>
  );
};

export default SingleSymbology;
